import { useEffect, useState } from "react";

import { api, ApiError } from "../lib/api";
import { useToast } from "../context/toast";
import { useT } from "../context/locale";
import type { TranslationKey } from "../i18n";
import { centsToInput, money, parseMoney } from "../lib/format";
import { BankField } from "./bank-field";
import {
  HistoryProfileForm,
  defaultProfile,
  profileIsValid,
} from "./history-profile-form";
import { Button, ErrorNote, Modal } from "./ui";
import {
  Checkbox,
  FieldGrid,
  MoneyField,
  SelectField,
  TextAreaField,
  TextField,
} from "./ui/form";
import type { AdminUser, HistoryProfile, UserStatus } from "../types/api";

/** Shared player create / edit / delete dialogs, used by Players and Test players. */

/** Every state an operator may put a player into. Labels resolve at render. */
const STATUS_VALUES: UserStatus[] = [
  "pending_review",
  "active",
  "banned",
  "rejected",
];

export function CreatePlayerDialog({
  open,
  onClose,
  onDone,
  defaultStatus = "active",
  title,
  note,
}: {
  open: boolean;
  onClose: () => void;
  onDone: (user: AdminUser) => void;
  defaultStatus?: UserStatus;
  /** Defaults to t("players.new") once the dictionary is reachable. */
  title?: string;
  note?: string;
}) {
  const t = useT();
  const heading = title ?? t("players.new");
  const toast = useToast();
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankAccountNo, setBankAccountNo] = useState("");
  const [password, setPassword] = useState("player12345");
  const [status, setStatus] = useState<UserStatus>(defaultStatus);
  const [balance, setBalance] = useState("0.00");
  /*
   * The history recipe is opt-in, and configured right here.
   *
   * Creating the account and giving it a past are one job — an operator standing
   * up a demo account should not have to create it, find it in the list, and
   * configure it all over again. Off by default, because most accounts made here
   * are not demos.
   */
  const [withHistory, setWithHistory] = useState(false);
  const [generateNow, setGenerateNow] = useState(true);
  const [profile, setProfile] = useState<HistoryProfile>(defaultProfile);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const balanceCents = parseMoney(balance);
  const valid =
    /^[a-zA-Z0-9_]{3,32}$/.test(username) &&
    fullName.trim().length > 0 &&
    password.length >= 8 &&
    balanceCents !== null &&
    balanceCents >= 0 &&
    (!withHistory || profileIsValid(profile));

  function close() {
    setUsername("");
    setFullName("");
    setPhone("");
    setBankName("");
    setBankAccountNo("");
    setPassword("player12345");
    setBalance("0.00");
    setStatus(defaultStatus);
    setWithHistory(false);
    setGenerateNow(true);
    setProfile(defaultProfile());
    setError(null);
    onClose();
  }

  async function submit() {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const user = await api.createUser({
        username: username.trim(),
        fullName: fullName.trim(),
        phone: phone.trim() || undefined,
        bankName: bankName.trim() || undefined,
        bankAccountNo: bankAccountNo.trim() || undefined,
        password,
        status,
        balanceCents: balanceCents ?? 0,
        historyProfile: withHistory ? profile : undefined,
        generateHistory: withHistory && generateNow,
      });

      /*
       * A failed generation is reported without losing the account.
       *
       * The player exists either way, and undoing the creation because its
       * history could not be written is the worse half to undo — the profile is
       * saved, so the operator can fix it and run it from the list.
       */
      toast.success(
        t("toast.playerCreated"),
        [
          `${user.username} starts with ${money(user.balanceCents)}.`,
          user.history
            ? t("toast.historyBuiltDetail", {
                bets: user.history.betsCreated,
                won: user.history.betsWon,
                deposits: user.history.depositCount,
                withdrawals: user.history.withdrawalCount,
                balance: money(user.history.balanceCents),
              })
            : null,
          user.historyError,
        ]
          .filter(Boolean)
          .join(" "),
      );
      onDone(user);
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.createPlayer"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      eyebrow={t("nav.group.people")}
      title={heading}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="gold"
            onClick={submit}
            loading={busy}
            disabled={!valid}
          >
            {t("audit.user.create")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {note ? <p className="text-muted text-[13px]">{note}</p> : null}

        <FieldGrid>
          <TextField
            label={t("common.username")}
            value={username}
            onChange={setUsername}
            hint={t("field.usernameHint")}
            error={
              username && !/^[a-zA-Z0-9_]{3,32}$/.test(username)
                ? "3–32 characters"
                : null
            }
            required
            autoFocus
          />
          <TextField
            label={t("common.fullName")}
            value={fullName}
            onChange={setFullName}
            required
          />
          <TextField label={t("common.phone")} value={phone} onChange={setPhone} />
          <BankField
            label={t("field.bankName")}
            value={bankName}
            onChange={setBankName}
          />
          <TextField
            label={t("field.bankAccountNo")}
            value={bankAccountNo}
            onChange={setBankAccountNo}
          />
          <TextField
            label={t("signIn.password")}
            value={password}
            onChange={setPassword}
            error={password.length < 8 ? t("field.minPassword") : null}
            required
          />
          <SelectField
            label={t("common.status")}
            value={status}
            onChange={setStatus}
            options={STATUS_VALUES.map((v) => ({
              value: v,
              label: t(`statusOption.${v}` as TranslationKey),
            }))}
            required
          />
          <MoneyField
            label={t("field.openingBalance")}
            value={balance}
            onChange={setBalance}
            hint={withHistory && generateNow ? t("history.openingBalanceHint") : undefined}
            required
          />
        </FieldGrid>

        <div
          className="space-y-3 rounded-lg px-4 py-3"
          style={{ background: "var(--surface-inset)" }}
        >
          <Checkbox
            label={t("history.enable")}
            hint={t("history.enableHint")}
            checked={withHistory}
            onChange={setWithHistory}
          />

          {withHistory ? (
            <>
              <p className="text-muted text-[12px]">{t("history.explain")}</p>
              <HistoryProfileForm value={profile} onChange={setProfile} />
              <Checkbox
                label={t("history.generateNow")}
                checked={generateNow}
                onChange={setGenerateNow}
              />
            </>
          ) : null}
        </div>

        {busy && withHistory && generateNow ? (
          <p className="text-dim text-[12px]">{t("history.working")}</p>
        ) : null}
        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

export function EditPlayerDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankAccountNo, setBankAccountNo] = useState("");
  const [status, setStatus] = useState<UserStatus>("active");
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    setFullName(user.fullName ?? "");
    setPhone(user.phone ?? "");
    setAddress(user.address ?? "");
    setBankName(user.bankName ?? "");
    setBankAccountNo(user.bankAccountNo ?? "");
    setStatus(user.status);
    setPassword("");
    setReason("");
    setError(null);
  }, [user]);

  const statusChanged = user ? status !== user.status : false;
  const valid =
    fullName.trim().length > 0 && (password === "" || password.length >= 8);

  async function submit() {
    if (!user || !valid) return;
    setBusy(true);
    setError(null);
    try {
      await api.updateUser(user.id, {
        fullName: fullName.trim(),
        phone: phone.trim() || null,
        address: address.trim() || null,
        bankName: bankName.trim() || null,
        bankAccountNo: bankAccountNo.trim() || null,
        status,
        password: password || undefined,
        reason: reason.trim() || undefined,
      });
      toast.success(
        t("toast.playerUpdated"),
        statusChanged
          ? t("players.statusNow", {
              status: t(`statusOption.${status}` as TranslationKey),
            })
          : undefined,
      );
      onDone();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.updatePlayer"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(user)}
      onClose={onClose}
      eyebrow={t("nav.group.people")}
      title={`Edit ${user?.username ?? ""}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="gold"
            onClick={submit}
            loading={busy}
            disabled={!valid}
          >
            {t("action.save")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FieldGrid>
          <TextField
            label={t("common.fullName")}
            value={fullName}
            onChange={setFullName}
            required
          />
          <TextField label={t("common.phone")} value={phone} onChange={setPhone} />
          <TextField label={t("field.address")} value={address} onChange={setAddress} />
          <BankField
            label={t("field.bankName")}
            value={bankName}
            onChange={setBankName}
          />
          <TextField
            label={t("field.bankAccountNo")}
            value={bankAccountNo}
            onChange={setBankAccountNo}
          />
          <SelectField
            label={t("common.status")}
            value={status}
            onChange={setStatus}
            options={STATUS_VALUES.map((v) => ({
              value: v,
              label: t(`statusOption.${v}` as TranslationKey),
            }))}
            required
          />
        </FieldGrid>

        {statusChanged && status === "banned" ? (
          <p
            className="rounded-lg px-3 py-2 text-[12px]"
            style={{ background: "var(--danger-bg)", color: "var(--danger)" }}
          >
            A banned player cannot sign in. Existing bets and balance are left
            untouched.
          </p>
        ) : null}

        <TextField
          label={t("field.resetPassword")}
          value={password}
          onChange={setPassword}
          placeholder={t("common.none")}
          hint={t("field.keepPassword")}
          error={
            password && password.length < 8 ? t("field.minPassword") : null
          }
        />

        <TextAreaField
          label={t("common.reason")}
          value={reason}
          onChange={setReason}
          placeholder={t("field.verifiedByPhone")}
          hint={t("common.reasonHint")}
        />

        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

export function DeletePlayerDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close() {
    setTyped("");
    setReason("");
    setError(null);
    onClose();
  }

  async function submit() {
    if (!user || typed !== user.username) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.deleteUser(user.id, reason.trim() || undefined);
      toast.success(
        t("toast.playerDeleted"),
        `${result.username}, ${result.betsRemoved} bets and ${result.transactionsRemoved} ledger rows removed.`,
      );
      onDone();
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.deletePlayer"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(user)}
      onClose={close}
      eyebrow={t("nav.group.people")}
      title={`Delete ${user?.username ?? ""}`}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={submit}
            loading={busy}
            disabled={typed !== user?.username}
          >
            {t("action.deletePermanently")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div
          className="rounded-lg px-4 py-3"
          style={{ background: "var(--surface-inset)" }}
        >
          <p className="eyebrow mb-2">{t("players.whatGoesWithThem")}</p>
          <ul className="text-muted space-y-1 text-[12px]">
            <li>
              · The account and its {money(user?.balanceCents ?? 0)} balance
            </li>
            <li>· Every bet they placed</li>
            <li>· Every wallet ledger row</li>
          </ul>
        </div>
        <p className="text-dim text-[12px]">
{t("players.deleteNote")}
        </p>

        <TextField
          label={`Type ${user?.username ?? ""} to confirm`}
          value={typed}
          onChange={setTyped}
          required
          autoFocus
        />
        <TextField
          label={t("common.reason")}
          value={reason}
          onChange={setReason}
          hint={t("common.reasonHint")}
        />

        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

export { centsToInput };
